import { useState } from "react";
import { useEntitlements } from "../lib/useEntitlements";
import { openBillingPortal } from "../lib/checkout";
import { Ic } from "./icons";

/**
 * Bannière persistante affichée en haut du shell quand `billing.status ===
 * "past_due"`. Survit aux navigations (Dashboard, Nouvelle, Fiches, Settings…)
 * et donne un bouton direct vers le Customer Portal pour mettre à jour la CB.
 *
 * Pas de dismiss possible : tant que le paiement n'est pas réglé, l'user voit
 * le rappel à chaque page. La création de fiche est bloquée côté serveur via
 * `consumeCreditForFiche`, mais surfacer l'état avant cette étape évite à
 * l'agent de remplir une fiche pour rien.
 */
export function PastDueBanner() {
  const { billing } = useEntitlements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (billing?.status !== "past_due") return null;

  async function handleOpenPortal() {
    setBusy(true);
    setError(null);
    try {
      await openBillingPortal();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Impossible d'ouvrir le portail.",
      );
      setBusy(false);
    }
  }

  return (
    <div
      role="alert"
      className="bg-amber-100 border-b border-amber-300 text-amber-950"
    >
      <div className="px-4 sm:px-6 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="w-7 h-7 rounded-full bg-amber-300 grid place-items-center shrink-0">
          <Ic.Bolt size={13} className="text-amber-900" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium leading-tight">
            Votre dernier paiement a échoué.
          </div>
          <div className="text-[12px] opacity-80 leading-tight mt-0.5">
            Mettez à jour votre moyen de paiement pour continuer à générer des
            fiches.
            {error && <span className="ml-2 text-rose-800">· {error}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={handleOpenPortal}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-ink-900 text-white text-[12.5px] font-medium hover:bg-ink-800 transition disabled:opacity-60"
        >
          {busy ? "Ouverture…" : "Ouvrir le portail"}
          <Ic.Arrow size={12} />
        </button>
      </div>
    </div>
  );
}
